import { Component, HostBinding, OnInit } from '@angular/core';
import { AuthorizationService } from '../../Services/authorization.service';

import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticatorService } from '../../Services/authenticator.service';

import { EncryptorService } from '../../Services/encryptor.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Title } from '@angular/platform-browser';
import { validatePassword } from '../../Validators/password.validator';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-restore-password',
    templateUrl: './restore-password.component.html',
    styleUrls: ['./restore-password.component.css'],
    standalone: false
})
export class RestorePasswordComponent implements OnInit {
    public id_user: string;
    hide: boolean = true;

    logos: string = '/assets/img/logos.png';

    themeKey = 'THEME';
    colorThemeKey = 'THEME-COLOR';
    mobile = false;
    maxWidth: string = '40%';
    padding: string = '0';

    @HostBinding('class') className = '';

    darkMode: boolean = true;
    defaultColor: boolean = true;

    formActualizacion: UntypedFormGroup;

    pageTitle: string = 'SCEU :: Recuperar contraseña';
    production: boolean = environment.production;
    check: boolean = environment.check;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        private title: Title,
        private authe: AuthenticatorService,
        private auth: AuthorizationService,
        private encryptorService: EncryptorService,
        private route: ActivatedRoute,
        private router: Router,
        private overlay: OverlayContainer
    ) {}

    ngOnInit(): void {
        this.id_user = this.authe.userRestorePassword.id;

        this.formActualizacion = this.formBuilder.group(
            {
                usuario: ['this.id_user', Validators.required],
                newPassword: ['', [Validators.required, validatePassword]],
                repeatedNewPassword: ['', Validators.required],
            },
            {
                validators: [this.matchPasswordsValidator],
            }
        );

        this.checkTheme();
        this.setTitle();
    }

    matchPasswordsValidator = (
        formGroup: AbstractControl
    ): ValidationErrors | null => {
        const passControlName = 'newPassword';
        const repeatedPassControlName = 'repeatedNewPassword';
        const pass1 = formGroup.get(passControlName)?.value;
        const pass2 = formGroup.get(repeatedPassControlName)?.value;

        if (pass1 !== pass2) {
            formGroup.get(repeatedPassControlName)?.setErrors({
                noMatch: true,
            });

            return { noMatch: true };
        }

        formGroup.get(repeatedPassControlName)?.setErrors(null);
        return null;
    };

    cambiar() {
        if (
            this.formActualizacion.controls.newPassword.value !=
            this.formActualizacion.controls.repeatedNewPassword.value
        ) {
            this.snackBar.open('Las contraseñas no coinciden', '🤖', {
                duration: 3000,
                verticalPosition: 'top',
                horizontalPosition: 'right',
            });
        } else {
            const newpassword = this.encryptorService.encrypt(
                this.formActualizacion.controls.newPassword.value
            );

            const bodyUpdate = {
                username: this.id_user,
                password: newpassword,
            };

            this.auth.resetPasswordToForgetPassword(bodyUpdate).subscribe(
                (res) => {
                    this.snackBar.open('Contraseña actualizada', '🤖', {
                        duration: 3000,
                        verticalPosition: 'top',
                        horizontalPosition: 'right',
                        panelClass: ['snack'],
                    });
                    this.authe.logOutRestorePassword();
                    this.router.navigate(['/login']);
                },
                (error) => {
                    this.snackBar.open('Ocurrio un error al actualizar', '🤖', {
                        duration: 3000,
                        verticalPosition: 'top',
                        horizontalPosition: 'right',
                        panelClass: ['snack-error-message'],
                    });
                }
            );
        }
    }

    cancelar() {
        this.authe.logOutRestorePassword();
        this.router.navigate(['login']);
    }

    setTheme(darkMode: boolean) {
        const darkClassName = 'darkMode';
        const darkDevClassName = 'darkMode-dev';
        const lightClassName = 'lightMode';
        const lightblueClassName = 'lightMode-default';
        const lightRedClassName = 'lightMode-red';
        const lightGreenClassName = 'lightMode-check';

        this.className = this.production
            ? darkMode
                ? darkClassName
                : lightClassName
            : this.check
            ? lightGreenClassName
            : darkDevClassName;

        localStorage.setItem(
            this.themeKey,
            this.encryptorService.aesEncrypt(this.className)
        );

        this.overlay
            .getContainerElement()
            .classList.remove(
                darkClassName,
                lightblueClassName,
                lightRedClassName,
                darkDevClassName
            );

        if (!this.production) {
            this.overlay.getContainerElement().classList.add(darkDevClassName);
            this.logos = '/assets/img/logos_white.png';
        } else if (darkMode) {
            this.overlay.getContainerElement().classList.add(darkClassName);
            this.logos = '/assets/img/logos_white.png';
        } else {
            this.checkColorLightTheme();
            this.logos = '/assets/img/logos.png';
        }
    }

    setColorLightTheme(defaultColor: boolean) {
        const lightClassName = 'lightMode';
        const lightblueClassName = 'lightMode-default';
        const lightRedClassName = 'lightMode-red';

        const redClassName = 'red';
        const blueClassName = 'blue';

        this.className = defaultColor ? lightblueClassName : lightRedClassName;

        const className = defaultColor ? blueClassName : redClassName;

        localStorage.setItem(
            this.colorThemeKey,
            this.encryptorService.aesEncrypt(className)
        );

        this.overlay
            .getContainerElement()
            .classList.remove(lightblueClassName, lightRedClassName);

        if (defaultColor) {
            this.overlay
                .getContainerElement()
                .classList.add(lightblueClassName);
        } else {
            this.overlay.getContainerElement().classList.add(lightRedClassName);
        }
    }

    checkTheme() {
        const classStoraged = localStorage.getItem(this.themeKey);
        const className = classStoraged
            ? this.encryptorService.aesDecrypt(classStoraged)
            : '';
        const darkMode = className == 'darkMode';

        this.setTheme(darkMode);

        this.darkMode = darkMode;
    }

    setTitle() {
        this.title.setTitle(this.pageTitle);
    }

    checkColorLightTheme() {
        const redClassName = 'red';

        const classStoraged = localStorage.getItem(this.colorThemeKey);
        const className = classStoraged
            ? this.encryptorService.aesDecrypt(classStoraged)
            : '';
        const defaultColor = className != redClassName;

        this.setColorLightTheme(defaultColor);

        this.defaultColor = defaultColor;
    }

    resize(width: number, height: number) {
        if (width <= 576) {
            this.maxWidth = '100%';
            this.padding = '30px';
        } else if (width <= 800) {
            this.maxWidth = '60%';
            this.padding = '20px';
        } else {
            this.maxWidth = '33%';
            this.padding = '10px';
        }

        // this.mobile = width <= 500 && height <= 850;
    }
    ngOnDestroy() {
        sessionStorage.clear();
    }
}
